import styled from "@emotion/styled";
import { useCallback, type RefObject } from "react";

import { FONT_WEIGHT_SEMI_BOLD, Icon, Tooltip } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { createFolderFilter } from "../../../filters";
import { useLandingPageSettings } from "../../../LandingPageSettingsContext";
import {
  CardContainer,
  DetailsContainer,
  GradientOverlay,
  IconContainer,
  IconWrapper,
  Thumbnail,
  Title,
} from "./GridCardElements";

const Container = styled(CardContainer)`
  position: relative;
  cursor: pointer;

  transition: transform 250ms ease-in-out;

  &:hover {
    transform: scale(1.05);

    transition: transform 125ms ease-in-out;

    ${GradientOverlay} {
      transform: translateY(25px);
    }
  }

  &.fade-enter {
  }

  &.fade-enter-active {
  }
`;

const ModuleCount = styled.span`
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  border-radius: 50px;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.generalTextIcon};
  height: 20px;
  width: 20px;
  line-height: 20px;
  text-align: center;
  font-size: 10px;
`;

const SearchCount = styled.span`
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

type Props = {
  title: string;
  filter: string;
  moduleCount: number;
  searchCount?: number;
  ref?: RefObject<HTMLDivElement | null>;
};

export const Folder = ({ title, filter, moduleCount, searchCount, ref }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const { addFilterSet } = useLandingPageSettings();

  const handleClick = useCallback(
    () => addFilterSet({ id: "folder", filters: [createFolderFilter(filter)], operator: "and", metadata: filter }),
    [addFilterSet, filter],
  );

  return (
    <Container onClick={handleClick} data-test="grid-folder-container" ref={ref}>
      <Thumbnail>
        <img src="/assets/warehouse.jpg" alt={formatMessage("folder")} />
      </Thumbnail>
      <GradientOverlay />
      <DetailsContainer>
        <Title>{title}</Title>
        <Tooltip title={formatMessage("contains", { moduleCount })}>
          <ModuleCount>{moduleCount}</ModuleCount>
        </Tooltip>
      </DetailsContainer>
      <IconContainer>
        <IconWrapper $show={!!searchCount}>
          <Icon icon="search" />
          <SearchCount>{searchCount ?? 0}</SearchCount>
        </IconWrapper>
      </IconContainer>
    </Container>
  );
};

const formatStrings = {
  contains: {
    en: "Folder contains {moduleCount, plural, =1 {# module} other {# modules}}.",
  },
  folder: {
    en: "Folder",
  },
} satisfies FormatMessages;
